import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Snackbar, Box } from '@mui/material'
import { useGetMyShiftsQuery, useReleaseShiftMutation } from '@/core/api'
import { GetMyShiftsParams } from '@/core/models/planning.model'
import MyShiftsList from '../components/MyShiftsList'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

export function MyShiftsContainer() {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [filters, setFilters] = useState<Partial<GetMyShiftsParams>>({})
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false)
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const authUser = useAppSelector(selectAuthUser)

  const { data, isLoading, error } = useGetMyShiftsQuery(
    {
      ...filters,
      page: Number(page),
      limit: Number(limit),
    },
    {
      // This ensures the component always shows fresh data
      refetchOnMountOrArgChange: true,
    }
  )

  const [releaseShift, { isLoading: isReleasingShift }] = useReleaseShiftMutation()

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handleFilterChange = (newFilters: Partial<GetMyShiftsParams>) => {
    setFilters(newFilters)
    setPage(1)
  }

  const handleReleaseShift = async (shiftId: string) => {
    if (!authUser) return
    try {
      await releaseShift({ shiftId, staffId: authUser._id }).unwrap()
      setSuccessSnackbarOpen(true)
    } catch (err: any) {
      setErrorMessage(err.data?.message || t('page.shiftMarketplace.errorOccurred', 'An error occurred'))
      setErrorSnackbarOpen(true)
    }
  }

  const handleCloseSuccessSnackbar = () => {
    setSuccessSnackbarOpen(false)
  }

  const handleCloseErrorSnackbar = () => {
    setErrorSnackbarOpen(false)
  }

  return (
    <>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <MyShiftsList
          shifts={data?.items || []}
          isLoading={isLoading || isReleasingShift}
          error={error}
          totalItems={data?.total || 0}
          page={page}
          limit={limit}
          onPageChange={handlePageChange}
          onFilterChange={handleFilterChange}
          onReleaseShift={handleReleaseShift}
          filters={filters}
        />
      </Box>

      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSuccessSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccessSnackbar} severity="success" sx={{ width: '100%' }}>
          {t('page.shiftMarketplace.shiftReleasedSuccess', 'Shift released successfully!')}
        </Alert>
      </Snackbar>

      <Snackbar open={errorSnackbarOpen} autoHideDuration={6000} onClose={handleCloseErrorSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseErrorSnackbar} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default MyShiftsContainer
